import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "@/components/Shared";

const STATS = [
  { id: "domains", value: 10, suffix: "+", label: "Deep-Tech Focus Domains", note: "AI · IoT · Robotics · Smart Cities · Fintech" },
  { id: "mous", value: 6, suffix: "", label: "MoUs & Institutional Alliances", note: "i-Hub · IIT Ropar · GSC Singapore · Inlighten London · NIELIT · STPI-NEXT" },
  { id: "sectors", value: 2, suffix: "×", label: "Sectors of Leadership", note: "Government of India × Private Analytics" },
  { id: "corridors", value: 4, suffix: "", label: "Global Market Corridors", note: "UK · UAE · Singapore · United States" },
];

const Counter = ({ value, suffix, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1500;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  useEffect(() => {
    const fallback = setTimeout(() => setDisplay(value), 3500);
    return () => clearTimeout(fallback);
  }, [value]);

  return (
    <span ref={ref} data-testid={testId} className="gold-text font-serif text-5xl font-semibold sm:text-6xl">
      {display}{suffix}
    </span>
  );
};

export default function Stats() {
  return (
    <section data-testid="stats-band" className="border-y border-[rgba(212,175,55,0.2)] bg-[#0A1222] py-16 md:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={`stat-${s.id}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className={i > 0 ? "lg:border-l lg:border-[rgba(212,175,55,0.15)] lg:pl-10" : ""}
          >
            <Counter value={s.value} suffix={s.suffix} testId={`stat-value-${s.id}`} />
            <Reveal delay={0.15 + i * 0.08}>
              <p className="mt-3 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#F7F5F0]">{s.label}</p>
              <p className="mt-2 text-xs leading-relaxed text-[#64748B]">{s.note}</p>
            </Reveal>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
